import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  Card, Table, Select, Button, InputNumber, Modal, message, Tag, Row, Col, 
  Upload, Typography, DatePicker, Radio, Space, Popconfirm, Badge, Input, Checkbox, Divider, Tooltip, Alert
} from "antd";
import { getDelegatesList } from "../../redux/slices/delegates/delegatesThunk";
import { getDelegateStock, adjustStock, importStock, getStockHistory } from "../../redux/slices/stocks/stocksThunk";
import { resetHistory } from "../../redux/slices/stocks/stocksSlice";
import { getSamples } from "../../redux/slices/samples/samplesThunk";
import { getAllDoctorsForFilter } from "../../redux/slices/persons/personsThunk";
import { 
  PlusOutlined, EditOutlined, UploadOutlined, InboxOutlined, HistoryOutlined, 
  DownloadOutlined, DeleteOutlined, SearchOutlined, 
  CloseOutlined, InfoCircleOutlined
} from "@ant-design/icons";
import axiosInstance from "../../services/axiosServicesVisit";
import dayjs from "dayjs";

const { Option } = Select;
const { Dragger } = Upload;
const { Text, Title } = Typography;

const StocksManagementPage = () => {
  const dispatch = useDispatch();

  // --- INITIALISATION ---
  useEffect(() => {
    dispatch(resetHistory()); 
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
    dispatch(getSamples({ page: 1, itemsPerPage: 1000 }));
    dispatch(getAllDoctorsForFilter());
  }, [dispatch]);
  
  // --- ÉTATS LOCAUX ---
  const [selectedDelegate, setSelectedDelegate] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs("2026-01-01"), dayjs()]);
  const [sampleSearch, setSampleSearch] = useState("");
  const [onlyPositiveStandard, setOnlyPositiveStandard] = useState(false);
  const [onlyPositiveSpecific, setOnlyPositiveSpecific] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [detailTitle, setDetailTitle] = useState("");

  const [importMonth, setImportMonth] = useState(dayjs());
  const [importType, setImportType] = useState("standard");
  const [fileList, setFileList] = useState([]);

  const [adjustDelegateId, setAdjustDelegateId] = useState(null);
  const [adjustIsSpecific, setAdjustIsSpecific] = useState(false);
  const [adjustItems, setAdjustItems] = useState([{ sampleId: null, quantity: 1, personId: null }]);
  const [existingSpecificStock, setExistingSpecificStock] = useState([]);
  const [stockPreviewLoading, setStockPreviewLoading] = useState(false);

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [adjustmentValues, setAdjustmentValues] = useState({ standard: 0, specific: 0, personId: null });

  // --- DATA REDUX ---
  const delegatesList = useSelector((state) => state.delegates.delegatesList?.data?.data || []);
  const samplesList = useSelector((state) => state.samples.samples?.data || []);
  const doctorsList = useSelector((state) => state.persons?.doctorsForFilter || []);
  const stockState = useSelector((state) => state.stocks);
  const stockData = stockState?.currentStock?.data || [];
  const stockLoading = stockState?.currentStock?.loading || false;
  const historyData = stockState?.history?.data || [];
  const historyLoading = stockState?.history?.loading || false;
  const importState = stockState?.importState || { loading: false };

  useEffect(() => {
    if (delegatesList.length > 0 && !selectedDelegate) setSelectedDelegate(delegatesList[0].id);
  }, [delegatesList, selectedDelegate]);

  // --- FONCTIONS DE CHARGEMENT ---
  const fetchExistingStockPreview = useCallback(async (delId) => {
    if (!delId) return;
    setStockPreviewLoading(true);
    try {
        // CORRECTIF : On utilise la route qui permet de voir les médecins
        const res = await axiosInstance.get(`/v1/delegate-stocks/specific-by-delegate/${delId}`);
        
        // Le backend renvoie maintenant directement doctorName, sampleName, etc.
        setExistingSpecificStock(res.data || []);
    } catch (error) { 
        console.error(error);
        message.error("Erreur lors du chargement du stock spécifique"); 
    }
    finally { setStockPreviewLoading(false); }
  }, []);

  const refreshData = useCallback(() => {
    if (selectedDelegate) {
      dispatch(getDelegateStock({ 
          delegateId: selectedDelegate, 
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD")
      }));
    }
  }, [selectedDelegate, dateRange, dispatch]);

  useEffect(() => { refreshData(); }, [refreshData]);

  // Chargement de l'aperçu quand on ouvre la modale ou change de délégué
  useEffect(() => {
    if (isModalVisible && adjustDelegateId) {
        fetchExistingStockPreview(adjustDelegateId);
    }
  }, [isModalVisible, adjustDelegateId, fetchExistingStockPreview]);

  // --- LOGIQUE FILTRAGE ET REGROUPEMENT ---
  const groupedStockData = useMemo(() => {
    const groups = {};
    stockData.forEach(item => {
      const sid = item.sample?.id;
      if (!sid) return;
      if (!groups[sid]) {
        groups[sid] = { ...item, initialQuantity: Number(item.initialQuantity || 0), quantity: Number(item.quantity || 0), initialSpecificQuantity: Number(item.initialSpecificQuantity || 0), specificQuantity: Number(item.specificQuantity || 0) };
      } else {
        groups[sid].initialQuantity += Number(item.initialQuantity || 0);
        groups[sid].quantity += Number(item.quantity || 0);
        groups[sid].initialSpecificQuantity += Number(item.initialSpecificQuantity || 0);
        groups[sid].specificQuantity += Number(item.specificQuantity || 0);
      }
    });
    return Object.values(groups);
  }, [stockData]);

  const filteredData = useMemo(() => {
    return groupedStockData.filter(item => {
      const name = item.sample?.name?.toLowerCase() || "";
      const matchesSearch = name.includes(sampleSearch.toLowerCase());
      let mStd = onlyPositiveStandard ? item.quantity > 0 : true;
      let mSpec = onlyPositiveSpecific ? item.specificQuantity > 0 : true;
      return matchesSearch && mStd && mSpec;
    });
  }, [groupedStockData, sampleSearch, onlyPositiveStandard, onlyPositiveSpecific]);

  // --- HANDLERS ACTIONS ---

  const handleOpenAdjustment = () => {
    setAdjustDelegateId(selectedDelegate);
    setAdjustIsSpecific(false);
    setAdjustItems([{ sampleId: null, quantity: 1, personId: null }]);
    setIsModalVisible(true);
  };

  const handleAdjustmentSubmit = async () => {
    if (!adjustDelegateId) return message.error("Veuillez choisir un délégué.");
    const validItems = adjustItems.filter(item => item.sampleId && item.quantity !== 0);
    if (validItems.length === 0) return message.error("Veuillez remplir au moins une ligne valide.");

    const hide = message.loading("Ajustement en cours...", 0);
    try {
      for (const item of validItems) {
        await dispatch(adjustStock({ ...item, delegateId: adjustDelegateId, isSpecific: adjustIsSpecific })).unwrap();
      }
      message.success("Mise à jour réussie !");
      setIsModalVisible(false);
      refreshData();
    } catch { message.error("Erreur lors de l'allocation."); }
    finally { hide(); }
  };

  const handleImportSubmit = () => {
    if (fileList.length === 0) return message.error("Fichier requis");
    const formData = new FormData();
    formData.append('file', fileList[0]);
    formData.append('isSpecific', importType === "specific" ? "true" : "false");
    dispatch(importStock(formData)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") { setIsImportModalVisible(false); setFileList([]); refreshData(); }
    });
  };

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    setAdjustmentValues({ standard: 0, specific: 0 }); // On repart de 0 pour l'ajustement
    setIsEditModalVisible(true);
  };


  const handleEditSubmit = async () => {
    // Validation : si on touche au spécifique, il faut un médecin
    if (adjustmentValues.specific !== 0 && !adjustmentValues.personId) {
      return message.warning("Veuillez sélectionner un médecin pour l'ajustement spécifique.");
    }

    const hide = message.loading("Mise à jour du stock...", 0);
    try {
      // 1. Ajustement Standard (toujours sans personId)
      if (adjustmentValues.standard !== 0) {
        await dispatch(adjustStock({ 
          delegateId: selectedDelegate, 
          sampleId: editingRecord.sample.id, 
          quantity: adjustmentValues.standard, 
          isSpecific: false,
          reason: "Ajustement manuel (Standard)"
        })).unwrap();
      }

      // 2. Ajustement Spécifique (AVEC personId)
      if (adjustmentValues.specific !== 0) {
        await dispatch(adjustStock({ 
          delegateId: selectedDelegate, 
          sampleId: editingRecord.sample.id, 
          quantity: adjustmentValues.specific, 
          isSpecific: true,
          personId: adjustmentValues.personId, // <--- IMPORTANT : On lie au médecin
          reason: "Ajustement manuel (Spécifique)"
        })).unwrap();
      }

      message.success("Stock mis à jour avec succès");
      setIsEditModalVisible(false);
      refreshData();
    } catch (error) {
      message.error("Erreur lors de la modification");
    } finally {
      hide();
    }
  };

  // --- COLONNES TABLEAU ---
  const mainColumns = [
    { 
      title: "Échantillon", 
      dataIndex: "sample", 
      render: (s, record) => (
        <span 
          onClick={() => { 
            setDetailTitle(`Historique : ${s?.name}`); 
            dispatch(resetHistory()); 
            dispatch(getStockHistory({ delegateId: selectedDelegate, sampleId: s?.id, startDate: dateRange[0].format("DD-MM-YYYY"), endDate: dateRange[1].format("DD-MM-YYYY") })); 
            setIsDetailModalVisible(true); 
          }} 
          style={{ fontWeight: 'bold', color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
        >{s?.name}</span>
      ) 
    },
    { 
      title: "Stock Standard", 
      children: [
        { title: "Alloué", dataIndex: "initialQuantity", align: "center" },
        { title: "Restant", dataIndex: "quantity", align: "center", render: q => <Tag color={q > 0 ? "green" : (q < 0 ? "red" : "default")}>{q}</Tag> }
      ]
    },
    { 
      title: "Stock Spécifique", 
      children: [
        { title: "Alloué", dataIndex: "initialSpecificQuantity", align: "center" },
        { title: "Restant", dataIndex: "specificQuantity", align: "center", render: q => <Tag color={q > 0 ? "purple" : "default"}>{q}</Tag> }
      ]
    },
    { 
    title: "Action", 
    align: "center", 
    render: (_, record) => (
      <Space size="middle">
        <Tooltip title="Modifier le stock">
          <Button 
            type="text" 
            icon={<EditOutlined style={{ color: '#1890ff' }} />} 
            onClick={() => handleOpenEdit(record)} 
          />
        </Tooltip>
        <Popconfirm 
          title="Supprimer ?" 
          onConfirm={async () => { await axiosInstance.delete(`/v1/delegate-stocks/${record.id}`); refreshData(); }} 
          okType="danger"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )
  }
];

  return (
    <div className="layout-content">
      <Card bordered={false} className="circlebox tablespace mb-24" title="Suivi des Stocks Délégués" extra={
          <Space wrap>
            <DatePicker.RangePicker format="DD-MM-YYYY" value={dateRange} onChange={(dates) => setDateRange(dates)} allowClear={false} style={{ width: 280 }} />
            <Button icon={<HistoryOutlined />} disabled={!selectedDelegate} onClick={() => { if(selectedDelegate) { dispatch(resetHistory()); dispatch(getStockHistory({ delegateId: selectedDelegate, sampleId: null, startDate: dateRange[0].format("DD-MM-YYYY"), endDate: dateRange[1].format("DD-MM-YYYY") })); setIsHistoryModalVisible(true); }}}>Historique Global</Button>
            <Button icon={<UploadOutlined />} onClick={() => setIsImportModalVisible(true)}>Importer CSV</Button>
            <Select showSearch style={{ width: 220 }} value={selectedDelegate} onChange={setSelectedDelegate} optionFilterProp="children">{delegatesList.map((d) => <Option key={d.id} value={d.id}>{`${d.firstname} ${d.lastname}`}</Option>)}</Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAdjustment}>Allocation Manuelle</Button>
          </Space>
      }>
        <div style={{ padding: "15px 24px", background: "#fafafa" }}>
          <Row gutter={16}>
            <Col md={6}><Input placeholder="Rechercher..." prefix={<SearchOutlined />} onChange={e => setSampleSearch(e.target.value)} /></Col>
            <Col md={18}><Space><Checkbox checked={onlyPositiveStandard} onChange={e => setOnlyPositiveStandard(e.target.checked)}>Standard {'>'} 0</Checkbox><Checkbox checked={onlyPositiveSpecific} onChange={e => setOnlyPositiveSpecific(e.target.checked)}>Spécifique {'>'} 0</Checkbox></Space></Col>
          </Row>
        </div>
        <Table columns={mainColumns} dataSource={filteredData} loading={stockLoading} pagination={false} bordered />
      </Card>

      {/* MODALE D'ALLOCATION MANUELLE */}
      <Modal 
        zIndex={1000} 
        title="Nouvelle Allocation" 
        open={isModalVisible} 
        onOk={handleAdjustmentSubmit} 
        onCancel={() => setIsModalVisible(false)} 
        width={900}
        okText="Valider l'allocation"
        cancelText="Annuler"
      >
        <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={12}>
                <Text strong>1. Délégué :</Text>
                <Select showSearch style={{ width: '100%', marginTop: 5 }} value={adjustDelegateId} onChange={setAdjustDelegateId} optionFilterProp="children">
                    {delegatesList.map(d => <Option key={d.id} value={d.id}>{`${d.firstname} ${d.lastname}`}</Option>)}
                </Select>
            </Col>
            <Col span={12}>
                <Text strong>2. Pool :</Text>
                <Radio.Group style={{ width: '100%', marginTop: 5 }} value={adjustIsSpecific} onChange={e => setAdjustIsSpecific(e.target.value)} buttonStyle="solid">
                    <Radio.Button value={false} style={{ width: '50%', textAlign: 'center' }}>Standard</Radio.Button>
                    <Radio.Button value={true} style={{ width: '50%', textAlign: 'center' }}>Spécifique</Radio.Button>
                </Radio.Group>
            </Col>
        </Row>
        
        <Divider orientation="left">3. Échantillons à ajouter</Divider>
        
        {adjustItems.map((item, index) => (
            <div key={index} style={{ background: '#f9f9f9', padding: 15, borderRadius: 8, marginBottom: 10 }}>
                <Row gutter={12} align="middle">
                    <Col span={adjustIsSpecific ? 8 : 14}>
                        <Select showSearch placeholder="Choisir Échantillon..." style={{ width: '100%' }} value={item.sampleId} onChange={v => { const n = [...adjustItems]; n[index].sampleId = v; setAdjustItems(n); }} optionFilterProp="children">
                            {samplesList.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                        </Select>
                    </Col>
                    {adjustIsSpecific && (
                        <Col span={8}>
                            <Select showSearch allowClear placeholder="Médecin (Optionnel)" style={{ width: '100%' }} value={item.personId} onChange={v => { const n = [...adjustItems]; n[index].personId = v; setAdjustItems(n); }} optionFilterProp="children">
                                {doctorsList.map(doc => <Option key={doc.id} value={doc.id}>{`${doc.firstname} ${doc.lastname}`}</Option>)}
                            </Select>
                        </Col>
                    )}
                    <Col span={4}>
                        <InputNumber min={-1000} style={{ width: '100%' }} value={item.quantity} onChange={v => { const n = [...adjustItems]; n[index].quantity = v; setAdjustItems(n); }} />
                    </Col>
                    <Col span={2}>
                        <Button type="text" danger icon={<CloseOutlined />} onClick={() => { const n = [...adjustItems]; n.splice(index,1); setAdjustItems(n); }} disabled={adjustItems.length === 1} />
                    </Col>
                </Row>
            </div>
        ))}
        
        <Button type="dashed" onClick={() => setAdjustItems([...adjustItems, {sampleId:null, quantity:1, personId:null}])} block icon={<PlusOutlined />} style={{ marginBottom: 20 }}>
            Ajouter une ligne d'échantillon
        </Button>

        {/* AFFICHAGE DES ÉCHANTILLONS DÉJÀ ASSIGNÉS (Anciens) */}
        {adjustIsSpecific && (
            <>
                <Divider orientation="left" style={{ marginTop: 30 }}>
                    <InfoCircleOutlined /> Stock Spécifique Actuel du Délégué
                </Divider>
                <Table 
                  loading={stockPreviewLoading} 
                  dataSource={existingSpecificStock} 
                  size="small" 
                  pagination={{ pageSize: 5 }}
                  rowKey="stockId"
                  columns={[
                      { 
                        title: 'Médecin / Contact', 
                        dataIndex: 'doctorName', // Le backend renvoie déjà le nom complet ici
                        render: (text) => <Text strong>{text}</Text>
                      },
                      { 
                        title: 'Échantillon', 
                        dataIndex: 'sampleName' // Le backend renvoie le nom de l'échantillon ici
                      },
                      { 
                        title: 'Restant', 
                        dataIndex: 'remaining', // Le backend renvoie la quantité restante ici
                        align: 'center', 
                        render: q => <Tag color="purple" style={{fontWeight: 'bold'}}>{q}</Tag> 
                      }
                  ]} 
              />
            </>
        )}
      </Modal>

      {/* AUTRES MODALES (Import, Historique...) */}
      <Modal title="Import CSV" open={isImportModalVisible} onOk={handleImportSubmit} onCancel={() => setIsImportModalVisible(false)} confirmLoading={importState.loading}>
        <Dragger onRemove={() => setFileList([])} beforeUpload={file => { setFileList([file]); return false; }} fileList={fileList} accept=".csv"><p className="ant-upload-drag-icon"><InboxOutlined /></p><p className="ant-upload-text">Cliquez ou déposez le fichier CSV ici</p></Dragger>
      </Modal>

      <Modal zIndex={1000} title="Historique Global" open={isHistoryModalVisible} onCancel={() => setIsHistoryModalVisible(false)} footer={null} width={1100}>
        <Table dataSource={historyData} loading={historyLoading} rowKey="id" size="small" columns={[{ title: "Date", dataIndex: "createdAt", render: d => dayjs(d).format("DD/MM/YYYY HH:mm") }, { title: "Échantillon", render: (_, r) => <Text strong>{r.sample?.name}</Text> }, { title: "Action", dataIndex: "type", render: (t) => <Tag color={t.includes("SORTIE") ? "volcano" : "green"}>{t}</Tag> }, { title: "Qté", dataIndex: "quantity", align:'center', render: q => <Text strong style={{ color: q > 0 ? 'green' : 'red' }}>{q > 0 ? `+${q}` : q}</Text> }, { title: "Raison", render: (_, r) => r.reason }]} />
      </Modal>

      <Modal zIndex={1000} title={detailTitle} open={isDetailModalVisible} onCancel={() => setIsDetailModalVisible(false)} footer={null} width={1000}>
        <Table dataSource={historyData} loading={historyLoading} rowKey="id" size="small" columns={[{ title: "Date", dataIndex: "createdAt", render: d => dayjs(d).format("DD/MM/YYYY HH:mm") }, { title: "Action", dataIndex: "type" }, { title: "Qté", dataIndex: "quantity", render: q => <Text strong style={{ color: q > 0 ? 'green' : 'red' }}>{q > 0 ? `+${q}` : q}</Text> }, { title: "Raison", render: (_, r) => r.reason }]} />
      </Modal>


      <Modal
  title={`Ajuster le stock : ${editingRecord?.sample?.name}`}
  open={isEditModalVisible}
  onOk={handleEditSubmit}
  onCancel={() => setIsEditModalVisible(false)}
  okText="Enregistrer"
  width={600}
>
  <Space direction="vertical" style={{ width: '100%' }} size="large">
    
    {/* SECTION STANDARD */}
    <Card size="small" title="Ajustement Stock Standard" headStyle={{ background: '#fafafa' }}>
        <Row gutter={16} align="middle">
            <Col span={16}><Text type="secondary">Modifier le stock général du délégué :</Text></Col>
            <Col span={8}>
                <InputNumber 
                    style={{ width: '100%' }} 
                    value={adjustmentValues.standard}
                    onChange={(val) => setAdjustmentValues(prev => ({ ...prev, standard: val || 0 }))}
                />
            </Col>
        </Row>
    </Card>

    {/* SECTION SPÉCIFIQUE */}
    <Card size="small" title="Ajustement Stock Spécifique" headStyle={{ background: '#f9f0ff' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">Sélectionner le médecin concerné :</Text>
            <Select 
                showSearch 
                placeholder="Choisir le médecin..." 
                style={{ width: '100%' }}
                value={adjustmentValues.personId}
                onChange={(val) => setAdjustmentValues(prev => ({ ...prev, personId: val }))}
                optionFilterProp="children"
            >
                {doctorsList.map(doc => (
                    <Option key={doc.id} value={doc.id}>{`${doc.firstname} ${doc.lastname}`}</Option>
                ))}
            </Select>
            
            <Row gutter={16} align="middle" style={{ marginTop: 10 }}>
                <Col span={16}><Text type="secondary">Quantité à ajuster (+ ou -) :</Text></Col>
                <Col span={8}>
                    <InputNumber 
                        style={{ width: '100%' }} 
                        value={adjustmentValues.specific}
                        onChange={(val) => setAdjustmentValues(prev => ({ ...prev, specific: val || 0 }))}
                    />
                </Col>
            </Row>
        </Space>
    </Card>

    <Alert 
      type="info" 
      showIcon 
      message="Note" 
      description="Les modifications impacteront le total 'Alloué' et le 'Restant' dans l'historique." 
    />
  </Space>
</Modal>
    </div>
  );
};

export default StocksManagementPage;